'use client'
import Image from '@/components/Image'
import { generateImagePath } from '@/utils/functions/helpers'
import clsx from 'clsx'
import { differenceInHours } from 'date-fns'
import Link from 'next/link'
import { PropsWithChildren, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PopupAsset } from '.'

export interface PopupProps {
    text: string
    textFontSize: string
    textColor: string
    title: string
    titleFontSize: string
    titleColor: string
    logo: PopupAsset
    image: PopupAsset
    pdf: PopupAsset
    buttonLink: string
    id: string
}

const popupSaleDontShowAgainLocalStorageId = 'popupSaleDontShowAnymore'
const popupSaleIdToHide24Hours = 'popupHidden24HoursId'
const popupTimeHiddenFor24LocalStorageId = 'popupTimeClosed'

export default function Popup({
    textFontSize,
    textColor,
    title,
    titleFontSize,
    titleColor,
    text,
    image,
    buttonLink,
    logo,
    id,
    pdf,
}: PropsWithChildren<PopupProps>) {
    const [open, setOpen] = useState<boolean>(false)
    const el = useRef<HTMLDivElement>(null)

    const hoursToHidePopup = 24

    const { t } = useTranslation()

    useEffect(() => {
        let canShow = true

        const hiddenForeverIds = JSON.parse(
            localStorage.getItem(popupSaleDontShowAgainLocalStorageId) || '[]'
        )
        if (hiddenForeverIds.includes(id)) {
            canShow = false
        }

        const hiddenId = localStorage.getItem(popupSaleIdToHide24Hours)
        const timeHidden = localStorage.getItem(
            popupTimeHiddenFor24LocalStorageId
        ) as string
        const timeHiddenDate = new Date(timeHidden)

        if (
            hiddenId === id &&
            differenceInHours(new Date(), timeHiddenDate) < hoursToHidePopup
        ) {
            canShow = false
        }

        setOpen(canShow)
    }, [id])

    function closeHide24Hours(): void {
        localStorage.setItem(
            popupTimeHiddenFor24LocalStorageId,
            new Date().toISOString()
        )
        localStorage.setItem(popupSaleIdToHide24Hours, id)
        setOpen(false)
    }

    function closeNeverShowAgain(): void {
        let hiddenForeverIds = JSON.parse(
            localStorage.getItem(popupSaleDontShowAgainLocalStorageId) || '[]'
        )
        if (!hiddenForeverIds.includes(id)) {
            hiddenForeverIds.push(id)
            localStorage.setItem(
                popupSaleDontShowAgainLocalStorageId,
                JSON.stringify(hiddenForeverIds)
            )
        }
        setOpen(false)
    }

    useEffect(() => {
        const handleDocumentClick = (event: MouseEvent) => {
            if (el.current && !el.current.contains(event.target as Node)) {
                setOpen(false)
            }
        }

        const handleEscPress = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setOpen(false)
            }
        }

        if (open) {
            document.body.style.overflow = 'hidden'
            document.addEventListener('mousedown', handleDocumentClick)
            document.addEventListener('keydown', handleEscPress)
        } else {
            document.body.style.overflow = 'unset'
        }

        return () => {
            document.body.style.overflow = 'unset'
            document.removeEventListener('mousedown', handleDocumentClick)
            document.removeEventListener('keydown', handleEscPress)
        }
    }, [open])

    if (!open) return null

    return (
        <div
            className={clsx(
                'fixed',
                'inset-0',
                'bg-black',
                'bg-opacity-50',
                'flex',
                'justify-center',
                'items-center',
                'z-[100]',
                'w-full',
                'h-full'
            )}
        >
            <div className="w-[85vw] max-h-[85vh] md:w-[60vw] md:max-h-[83vh] xl:max-h-[83vh] xl:w-[40vw] bg-white rounded-lg relative mx-5 p-6 overflow-y-scroll">
                <div ref={el} className="flex flex-col justify-center ">
                    <Image
                        className="mx-auto h-[50px] pt-2 mb-5"
                        path={generateImagePath(logo.path)}
                        alt={t('eshop.popup.logoAlt')}
                    />
                    <Image
                        className="mx-auto max-h-[200px]"
                        path={generateImagePath(image.path)}
                        alt={t('eshop.popup.imageAlt')}
                    />
                    <div className="py-4 px-7 text-center">
                        <h2 className="text-2xl sm:text-3xl lg:text-4xl">
                            {t('eshop.popup.title')}
                        </h2>
                        <h2
                            style={{
                                ...(titleFontSize
                                    ? { fontSize: titleFontSize }
                                    : {}),
                                ...(titleColor ? { color: titleColor } : {}),
                            }}
                            className="text-xl"
                        >
                            {title}
                        </h2>
                        <p
                            className="text-md p-8 text-blue-dark"
                            style={{
                                ...(textFontSize
                                    ? { fontSize: textFontSize }
                                    : {}),
                                ...(textColor ? { color: textColor } : {}),
                            }}
                        >
                            {text}
                        </p>
                    </div>
                    <div className="flex-col lg:flex-row flex justify-center gap-1 flex-wrap">
                        <Link
                            href={buttonLink}
                            className="btn font-normal scale-100 indicator px-8 btn-secondary w-[50%] lg:w-auto mx:auto self-center"
                        >
                            {t('eshop.popup.shopLinkText')}
                        </Link>
                        <Link
                            href={
                                process.env.NEXT_PUBLIC_CMS_ASSET_URL + pdf.path
                            }
                            target="_blank"
                            className="btn font-normal scale-100 text-blue-dark text-base btn-link [text-underline-offset:6px] pr-6 pl-6 lg:w-auto mx:auto w-[50%] self-center"
                        >
                            {t('eshop.popup.pdfText')}
                        </Link>
                        <button
                            onClick={closeNeverShowAgain}
                            className="btn btn-outline lg:w-auto mx:auto self-center min-w-[50%]"
                        >
                            {t('eshop.popup.dontShowAgainText')}
                        </button>
                    </div>
                    <button
                        onClick={closeHide24Hours}
                        className="btn btn-sm btn-circle btn-ghost absolute right-2 top-2"
                    >
                        ✕
                    </button>
                </div>
            </div>
        </div>
    )
}
